import React, {FormEvent, useEffect, useState} from "react";
import {ActionRow, ActionRowButton, InputLabel, Modal, TextField} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {DiscountType, MAX_DISCOUNT_EURO, MAX_DISCOUNT_PERCENTAGE, PosOrderDiscount,} from "@/data/entities/order";

type Props = {
    show: boolean,
    setShow: (show: boolean) => void,
    // Current discount of the order, to change it; null when adding one
    current: PosOrderDiscount | null,
    onApply: (discount: PosOrderDiscount) => void,
};

/** Value as typed by the employee, e.g. "5", "2,50" or "10.5". */
const parseValue = (input: string): number => Number(input.trim().replace(",", "."));

/** Value to show in the field, with a decimal comma. */
const formatValue = (value: number): string => String(value).replace(".", ",");

/**
 * Error message ID for the typed value when applied as the given type, or
 * null when valid.
 */
const validate = (input: string, type: DiscountType): string | null => {
    const value: number = parseValue(input);
    if (input.trim() === "" || !Number.isFinite(value) || Math.round(value * 100) <= 0)
        return "discount.invalid";

    if (type === "PERCENTAGE" && value > MAX_DISCOUNT_PERCENTAGE)
        return "discount.percentageTooHigh";

    if (type === "EURO" && value > MAX_DISCOUNT_EURO)
        return "discount.tooHigh";

    return null;
};

/**
 * The employee enters a number and applies it either as an amount of money
 * or as a percentage of the total.
 */
const DiscountModal = ({show, setShow, current, onApply}: Props) => {
    const intl = useIntl();
    const [value, setValue] = useState<string>("");
    const [errorMsgId, setErrorMsgId] = useState<string | null>(null);

    // Start with the current discount (or empty) every time the modal opens
    useEffect(() => {
        if (!show) return;
        setValue(current === null ? "" : formatValue(current.value));
        setErrorMsgId(null);
    }, [show, current]);

    const apply = (type: DiscountType) => {
        const error: string | null = validate(value, type);
        setErrorMsgId(error);
        if (error !== null)
            return;

        // Whole cents (or hundredths of a percent), like the backend expects
        onApply({type, value: Math.round(parseValue(value) * 100) / 100});
        setShow(false);
    };

    const applyButton = (type: DiscountType) => (
        <ActionRowButton
            selected={type === current?.type}
            color={"secondary"}
            buttonAction={{
                as: "button",
                actionProps: {
                    onClick: e => {
                        e.preventDefault();
                        apply(type);
                    },
                },
            }}
        >
            <FormattedMessage id={type === "EURO" ? "applyAsEuroDiscount" : "applyAsPercentageDiscount"}/>
        </ActionRowButton>
    );

    return (
        <Modal
            show={show}
            onClose={() => setShow(false)}
            title={intl.formatMessage({id: current === null ? "addDiscount" : "editDiscount"})}
            closeButtonTitle={intl.formatMessage({id: "back"})}
            position={"center-bottom"}
            breakpoints={{md: {position: "center"}}}
            actionRow={
                <ActionRow>
                    {applyButton("PERCENTAGE")}
                    {applyButton("EURO")}
                </ActionRow>
            }
        >
            {/* Enter is ambiguous (euro or percentage), so it doesn't submit */}
            <form
                className={"flex flex-col gap-4 mt-4"}
                onSubmit={(e: FormEvent) => e.preventDefault()}
            >
                <p>
                    <FormattedMessage id={"discountDescription"}/>
                </p>

                <TextField
                    type={"text"}
                    inputMode={"decimal"}
                    autoComplete={"off"}
                    autoFocus={true}
                    label={
                        <InputLabel>
                            <FormattedMessage id={"discountValue"}/>
                        </InputLabel>
                    }
                    placeholder={intl.formatMessage({id: "discountValuePlaceholder"})}
                    value={value}
                    onChange={e => {
                        setValue(e.target.value);
                        setErrorMsgId(null);
                    }}
                    error={errorMsgId === null ? undefined : intl.formatMessage({id: errorMsgId})}
                />
            </form>
        </Modal>
    );
};

export default DiscountModal;